import { useEffect, useMemo, useRef } from "react";
import { LoadingSpinner } from "../../components/ui/loadingSpinner/loadingSpinner";
import { useParams } from "react-router-dom";
import { useAudit } from "../../contexts/auditContext";
import FileViewer from "../extraction/viewExtraction/FileViewer.tsx";
import {
  ResizablePanel,
  ResizablePanelGroup,
  ResizableHandle,
} from "../../components/ui/resizable.tsx";
import { AuditSessionTabs } from "../../components/audit/AuditSessionTabs";
import AuditPageHeader from "../../components/audit/AuditPageHeader";
import { AuditSessionContent } from "../../components/audit/AuditSessionContent";
import { DocumentProcessingToast } from "../../components/audit/DocumentPipelineView";
import { AuditUploadFile } from "./auditSampleTest/components/AuditUploadFile";
import { AuditUploadGuidanceDoc } from "./auditSampleTest/components/AuditUploadGuidanceDoc";
import { useDocumentProcessingStatus } from "../../hooks/useDocumentProcessingStatus";
import { PermissionGate } from "../../components/PermissionGate";
import { useControlSessionStatus } from "../../hooks/audit/useControlSessionStatus";
import { useHookCache } from "../../lib/hookCache";
import { useSessionAnalyticsOwner } from "../../hooks/audit/useSessionAnalyticsOwner";

export const AuditSessionPageV2 = () => {
  const { controlSessionId } = useParams();
  const {
    selectedDocument,
    updateCurrentDocument,
    currentSelectedDocumentUrl,
    documentUploadNonce,
    controlSessionLoading,
    controlSession,
    sessionKind,
  } = useAudit();
  const analyticsOwner = useSessionAnalyticsOwner();
  const { invalidate } = useHookCache();
  const sessionRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Smart polling + per-procedure cache invalidation on transitions.
  useControlSessionStatus(controlSessionId, sessionKind, analyticsOwner);

  const { isProcessing: isDocumentsProcessing } = useDocumentProcessingStatus(
    controlSessionId ?? null,
    documentUploadNonce,
  );

  useEffect(() => {
    updateCurrentDocument(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When document processing finishes (isProcessing transitions true → false),
  // invalidate the per-procedure caches to pick up new documents / matches.
  const prevIsProcessingRef = useRef<boolean>(false);
  useEffect(() => {
    const wasProcessing = prevIsProcessingRef.current;
    prevIsProcessingRef.current = isDocumentsProcessing;
    if (!controlSessionId) return;
    if (wasProcessing && !isDocumentsProcessing) {
      void invalidate(["control-session", controlSessionId]);
    }
  }, [isDocumentsProcessing, controlSessionId, invalidate]);

  const enabledProcedureSessions = useMemo(() => {
    if (!controlSession) return [];
    return controlSession.procedureSessions
      .filter((session) => session.procedure.isEnabled)
      .sort((a, b) => a.procedure.procedureOrder - b.procedure.procedureOrder);
  }, [controlSession]);

  const scrollToSession = (index: number) => {
    const sessionElement = sessionRefs.current[index];
    if (sessionElement) {
      sessionElement.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Gate on data presence, not `isLoading`. The envelope is invalidated
  // when document processing finishes (see useEffect above), which flips
  // `controlSessionLoading` to true under the new `useHookCacheQuery`
  // semantics while keeping the cached envelope data populated. Gating on
  // the loading flag would unmount the entire page for what should be an
  // invisible background refetch — losing child state (selected sample)
  // and flashing the spinner over the existing UI.
  if (controlSessionLoading && !controlSession) {
    return (
      <div className="flex justify-center items-center h-screen w-full">
        <LoadingSpinner />
      </div>
    );
  }

  const leftPanel = (
    <div className="flex flex-col w-full mx-auto mb-8 h-screen overflow-y-auto pb-16">
      <div className="px-4 mx-auto flex flex-col w-full mt-4">
        <AuditPageHeader />
        <div className="sticky top-0 z-10 bg-white pt-4 pb-4">
          <div className="flex items-center justify-between mb-4">
            <AuditSessionTabs activeTab="support" v2={true} />
          </div>
          {controlSession?.status !== "complete" && (
            <PermissionGate permission="run_test">
              <div className="flex flex-row gap-2 mt-4">
                <AuditUploadFile buttonText="Sample-Specific Evidence" />
                <AuditUploadGuidanceDoc />
              </div>
            </PermissionGate>
          )}
        </div>
        <div className="mt-1"></div>
        {enabledProcedureSessions.map((session, index) => {
          return (
            <div
              key={session.id}
              ref={(el) => (sessionRefs.current[index] = el)}
            >
              <AuditSessionContent
                procedureSessionId={session.id}
                currentIndex={index}
                totalSessions={enabledProcedureSessions.length}
                onNavigate={scrollToSession}
                isDocumentsProcessing={isDocumentsProcessing}
              />
            </div>
          );
        })}
      </div>
    </div>
  );

  const rightPanel = selectedDocument ? (
    <div className="flex flex-col h-full">
      {selectedDocument && (
        <>
          <div className="flex-1 overflow-y-auto">
            {currentSelectedDocumentUrl && selectedDocument.fileType && (
              <FileViewer
                signedUrl={currentSelectedDocumentUrl?.url}
                fileType={selectedDocument.fileType}
                initialPage={1}
                videoDocument={currentSelectedDocumentUrl?.videoDocument}
              />
            )}
          </div>
        </>
      )}
    </div>
  ) : null;

  return (
    <div className="h-screen w-[calc(100%-63px)]">
      <ResizablePanelGroup
        className="w-full max-w-[2500px] mx-auto"
        autoSaveId="conditional"
        direction="horizontal"
      >
        <ResizablePanel id="left-panel" order={1} defaultSize={70}>
          {leftPanel}
        </ResizablePanel>
        {selectedDocument && (
          <>
            <ResizableHandle withHandle />
            <ResizablePanel id="right-panel" order={2} defaultSize={30}>
              {rightPanel}
            </ResizablePanel>
          </>
        )}
      </ResizablePanelGroup>
      <DocumentProcessingToast
        subjectId={controlSessionId ?? null}
        scope={
          sessionKind === "work_step" ? "work-step-session" : "control-session"
        }
        reconnectKey={documentUploadNonce}
      />
    </div>
  );
};
